import { useState, useEffect } from 'react';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { toast } from 'sonner';
import { Plus, Trash2 } from 'lucide-react';

const API_URL = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function AccessZonesManager({ eventId }) {
  const [zones, setZones] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    order: 0
  });

  useEffect(() => {
    fetchZones();
  }, [eventId]);

  const fetchZones = async () => {
    try {
      const response = await axios.get(`${API_URL}/events/${eventId}/access-zones`);
      setZones(response.data);
    } catch (error) {
      console.error('Error fetching zones:', error);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      await axios.post(`${API_URL}/events/${eventId}/access-zones`, formData);
      toast.success('Zona di accesso creata!');
      setShowForm(false);
      setFormData({ name: '', description: '', order: 0 });
      fetchZones();
    } catch (error) {
      toast.error('Errore durante la creazione');
    }
  };

  const handleDelete = async (zoneId) => {
    if (!window.confirm('Sei sicuro di voler eliminare questa zona?')) return;
    try {
      await axios.delete(`${API_URL}/access-zones/${zoneId}`);
      toast.success('Zona eliminata');
      fetchZones();
    } catch (error) {
      toast.error('Errore durante l\'eliminazione');
    }
  };

  return (
    <div className="space-y-6" data-testid="access-zones-manager">
      <div className="flex justify-between items-center">
        <h2 className="text-2xl font-bold text-gray-900">Zone di Accesso</h2>
        <Button onClick={() => setShowForm(!showForm)} data-testid="add-zone-button">
          <Plus className="w-4 h-4 mr-2" />
          Aggiungi Zona
        </Button>
      </div>

      {showForm && (
        <Card>
          <CardHeader>
            <CardTitle>Nuova Zona di Accesso</CardTitle>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div>
                <Label htmlFor="zone-name">Nome *</Label>
                <Input
                  id="zone-name"
                  value={formData.name}
                  onChange={(e) => setFormData({...formData, name: e.target.value})}
                  required
                  data-testid="zone-name-input"
                  placeholder="Es: Ingresso Principale, Zona Ristoro"
                />
              </div>
              <div>
                <Label htmlFor="zone-description">Descrizione</Label>
                <Textarea
                  id="zone-description"
                  value={formData.description}
                  onChange={(e) => setFormData({...formData, description: e.target.value})}
                  data-testid="zone-description-input"
                />
              </div>
              <div className="flex gap-2">
                <Button type="submit" data-testid="save-zone-button">Salva</Button>
                <Button type="button" variant="outline" onClick={() => setShowForm(false)}>Annulla</Button>
              </div>
            </form>
          </CardContent>
        </Card>
      )}

      {zones.length === 0 ? (
        <Card>
          <CardContent className="py-12 text-center">
            <p className="text-gray-600">Nessuna zona di accesso ancora. Inizia creandone una!</p>
          </CardContent>
        </Card>
      ) : (
        <div className="grid gap-4">
          {zones.map(zone => (
            <Card key={zone.id} data-testid={`zone-${zone.id}`}>
              <CardContent className="p-6">
                <div className="flex justify-between items-start">
                  <div>
                    <h3 className="text-xl font-semibold text-gray-900 mb-2">{zone.name}</h3>
                    <p className="text-gray-600">{zone.description}</p>
                  </div>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => handleDelete(zone.id)}
                    className="text-red-600 hover:text-red-700 hover:bg-red-50"
                    data-testid={`delete-zone-${zone.id}`}
                  >
                    <Trash2 className="w-4 h-4" />
                  </Button>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
}
